/**
 * What a doctor fills in for each record type.
 *
 * This mirrors WRITABLE_FIELDS in backend/clinical/serializers.py, and it is a
 * convenience rather than a rule. The server decides what it accepts; a field
 * missing here means a doctor cannot type it, never that the API would take it.
 *
 * Keeping the two in step is a small cost. Generating this from the contract
 * is not possible while `payload` is a free-form object, which it is because
 * one endpoint writes four different shapes (DSN-HLT-002 section 3.2).
 */

export type FieldKind = "text" | "number" | "date" | "datetime" | "choice";

export interface Field {
  name: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  choices?: { value: string; label: string }[];
  hint?: string;
}

export interface RecordShape {
  type: "observation" | "condition" | "medication";
  label: string;
  /** What to call one of these in a sentence a doctor reads. */
  article: string;
  fields: Field[];
}

export const RECORD_SHAPES: RecordShape[] = [
  {
    type: "observation",
    label: "Measurement",
    article: "a measurement",
    fields: [
      {
        name: "display",
        label: "What was measured",
        kind: "text",
        required: true,
        hint: "For example: Systolic blood pressure",
      },
      {
        name: "code",
        label: "LOINC code",
        kind: "text",
        required: true,
        hint: "For example: 8480-6",
      },
      { name: "value_quantity", label: "Value", kind: "number" },
      { name: "unit", label: "Unit", kind: "text", hint: "For example: mmHg" },
      {
        name: "value_text",
        label: "Or a value in words",
        kind: "text",
        hint: "For a result that is not a number, such as Positive",
      },
      { name: "effective_datetime", label: "When it was measured", kind: "datetime", required: true },
    ],
  },
  {
    type: "condition",
    label: "Condition",
    article: "a condition",
    fields: [
      {
        name: "display",
        label: "Condition",
        kind: "text",
        required: true,
        hint: "For example: Type 2 diabetes",
      },
      { name: "code", label: "ICD-10 code", kind: "text", hint: "For example: E11" },
      {
        name: "clinical_status",
        label: "Status",
        kind: "choice",
        choices: [
          { value: "active", label: "Active" },
          { value: "resolved", label: "Resolved" },
        ],
      },
      { name: "onset_date", label: "Onset", kind: "date" },
    ],
  },
  {
    type: "medication",
    label: "Medication",
    article: "a medication",
    fields: [
      {
        name: "medication_text",
        label: "Medication",
        kind: "text",
        required: true,
        hint: "For example: Metformin",
      },
      {
        name: "dosage_text",
        label: "Dosage",
        kind: "text",
        hint: "For example: 500 mg twice daily",
      },
      {
        name: "status",
        label: "Status",
        kind: "choice",
        choices: [
          { value: "active", label: "Active" },
          { value: "stopped", label: "Stopped" },
          { value: "completed", label: "Completed" },
        ],
      },
      { name: "medication_code", label: "Code", kind: "text" },
      { name: "effective_period_start", label: "Started", kind: "date" },
      { name: "effective_period_end", label: "Ended", kind: "date" },
    ],
  },
];

export function shapeFor(type: string): RecordShape | undefined {
  return RECORD_SHAPES.find((shape) => shape.type === type);
}

/**
 * A nurse records measurements. Everything else needs a doctor (CLN-F-013).
 *
 * This decides what the portal offers, never what the API allows. The server
 * checks the same rule and its answer is the one that counts.
 */
export function shapesFor(role: string | undefined): RecordShape[] {
  if (role === "doctor") return RECORD_SHAPES;
  if (role === "nurse") return RECORD_SHAPES.filter((shape) => shape.type === "observation");
  return [];
}

/**
 * Fill a form from a record that already exists, for a correction.
 *
 * A datetime arrives as an ISO string and the input wants local time with no
 * zone, so it is trimmed to the minute.
 */
export function valuesFrom(payload: Record<string, unknown>, shape: RecordShape) {
  const values: Record<string, string> = {};
  for (const field of shape.fields) {
    const value = payload[field.name];
    if (value === null || value === undefined) continue;
    values[field.name] =
      field.kind === "datetime" ? String(value).slice(0, 16) : String(value);
  }
  return values;
}
